import { DateFormatter, DueDateFormatter, Translate } from '@components'

import { Icon, InternalAccessor, ItemsPerPage, Table, TableColumn } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Row } from 'react-table'

import { CegidIntegrationStatus } from '@features/cegidIntegration/elements/CegidIntegrationStatus'
import { CegidIntegrationStatusHeader } from '@features/cegidIntegration/elements/CegidIntegrationStatusHeader'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { CustomEvent } from '../../../../../../../enums/customEvent'
import { QueryKeys } from '../../../../../../../enums/queryKeys'
import { SortDirection } from '../../../../../../../enums/sortDirection'
import { TableIds } from '../../../../../../../enums/tableIds'
import { useCustomEventListener } from '../../../../../../../hooks/useCustomEventListener'
import { useIsAutoCegidIntegrationEnabled } from '../../../../../../../hooks/useIsAutoCegidIntegrationEnabled'
import { getTableColumnIdDirection } from '../../../../../../../utils/getTableColumnIdDirection'
import { useBillsTableData } from '../../../../../hooks/useBillsTableData'
import { BillsSortProperty } from '../../../../../query-api'
import { BillsTableAccessor } from '../../enums/billsTableAccessor'
import { useBills } from '../../hooks/useBills'
import { useBillsListFilters } from '../../hooks/useBillsListFilters'
import { BillsTableRow } from '../../types/billsTableRow'
import { getBillQueryData } from '../../utils/getBillQueryData'
import { BillDescription } from '../BillDescription'
import { BillStatus } from '../BillStatus'
import { BillsTableHoverActions } from './elements/BillsTableHoverActions'
import { BillsTableStaticActions } from './elements/BillsTableStaticActions'
import { SupplierColumnTitle } from './elements/SupplierColumnTitle'

/**
 * @deprecated Part of the legacy Bills list view
 */
export const BillsTable = (): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { bills, contacts, isLoading, pagination } = useBills()
  const data = useBillsTableData(bills, contacts)
  const [{ pageSize, sortDirection, sortProperty }, setQueryParams] = useBillsListFilters()
  const queryClient = useQueryClient()
  const { isAutoCegidIntegrationEnabled } = useIsAutoCegidIntegrationEnabled()

  const isCegidOrganization = organization?.isCegid

  const columns: TableColumn<BillsTableRow>[] = useMemo(
    () => [
      {
        accessor: BillsTableAccessor.Status,
        alignment: 'center',
        Cell: ({ value: status }) => <BillStatus status={status} />,
        size: 'xxxxs',
        sortable: false,
      },
      {
        accessor: BillsTableAccessor.Attachment,
        alignment: 'center',
        Cell: ({ value: hasAttachment }) => hasAttachment && <Icon icon="paperclip" />,
        Header: <Translate value="bills.table.column.attachment" />,
        size: 'xxs',
        sortable: true,
        truncate: true,
      },
      {
        accessor: BillsTableAccessor.VoucherNo,
        Header: <Translate value="bills.table.column.voucher_no" />,
        size: 'xs',
        sortable: true,
        truncate: true,
      },
      {
        accessor: BillsTableAccessor.Date,
        Cell: ({ value: date }) => date && <DateFormatter value={date} />,
        fixedSize: true,
        Header: <Translate value="bills.table.column.date" />,
        size: 's',
        sortable: true,
      },
      {
        accessor: BillsTableAccessor.DueDate,
        Cell: ({ value: dueDate }) => dueDate && <DueDateFormatter value={dueDate} />,
        fixedSize: true,
        Header: <Translate value="bills.table.column.due_date" />,
        size: 's',
        sortable: true,
        truncate: true,
      },
      {
        accessor: BillsTableAccessor.RefNo,
        Header: <Translate value="bills.table.column.ref_no" />,
        hidden: true,
        size: 'xs',
        sortable: false,
        truncate: true,
      },
      {
        accessor: BillsTableAccessor.Description,
        Cell: ({ value: descriptionData }) =>
          descriptionData ? (
            <BillDescription isCreditNote={descriptionData.isCreditNote} title={descriptionData.value}>
              {descriptionData.value}
            </BillDescription>
          ) : (
            ''
          ),
        Header: <Translate value="bills.table.column.description" />,
        size: 'm',
        sortable: true,
        truncate: true,
      },
      {
        // It's well known issue in react-table... Link: https://github.com/TanStack/table/issues/3004
        accessor: (accessors) => accessors[BillsTableAccessor.Vendor],
        Header: <SupplierColumnTitle isAutoCegidIntegrationEnabled={isAutoCegidIntegrationEnabled} />,
        id: BillsTableAccessor.Vendor,
        shouldWrapInText: false,
        size: 's',
        sortable: true,
        truncate: true,
      },
      ...(isCegidOrganization
        ? [
            {
              accessor: BillsTableAccessor.IntegrationStatus,
              alignment: 'center',
              Header: <CegidIntegrationStatusHeader tooltip={t('cegid_integration_status.table.purchase.tooltip')} />,
              Cell: ({ value: integrationStatus }) => {
                return integrationStatus && <CegidIntegrationStatus integrationStatus={integrationStatus} />
              },
              size: 'xxs',
              sortable: false,
            } as TableColumn<BillsTableRow>,
          ]
        : []),
      {
        accessor: BillsTableAccessor.ExcludedVat,
        alignment: 'right',
        Header: <Translate value="bills.table.column.excluded_vat" />,
        size: 's',
        sortable: true,
      },
      {
        accessor: BillsTableAccessor.IncludedVat,
        alignment: 'right',
        Header: <Translate value="bills.table.column.included_vat" />,
        size: 's',
        sortable: true,
      },
      {
        accessor: BillsTableAccessor.Balance,
        alignment: 'right',
        Header: <Translate value="bills.table.column.balance" />,
        hidden: true,
        size: 's',
        sortable: true,
      },
      {
        accessor: InternalAccessor.UrlInternal,
      },
      {
        accessor: InternalAccessor.HoverActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { id, bill } = data[row.id] as BillsTableRow

          return <BillsTableHoverActions bill={bill} billId={id} />
        },
        sortable: false,
      },
      {
        accessor: InternalAccessor.StaticActions,
        Header: '',
        Cell: ({ row, data }) => {
          const { bill, id, paymentId, staticActionsItems } = data[row.id] as BillsTableRow

          return <BillsTableStaticActions bill={bill} billId={id} items={staticActionsItems} paymentId={paymentId} />
        },
        sortable: false,
      },
    ],
    [isCegidOrganization, isAutoCegidIntegrationEnabled, t],
  )

  const sortedColumnId = useMemo(
    () => getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const handleRowClick = useCallback(
    (row: Row<BillsTableRow>) => {
      const { id: billId } = row.original
      const billQueryData = getBillQueryData(billId, bills, contacts)
      queryClient.setQueryData([QueryKeys.Bill, billId], () => billQueryData)
    },
    [bills, contacts, queryClient],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setQueryParams({ page })
    },
    [setQueryParams],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setQueryParams({ page: 1, pageSize: itemsPerPage })
    },
    [setQueryParams],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setQueryParams({
        page: 1,
        sortProperty: columnId as BillsSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setQueryParams],
  )

  // Handle ember's Register Payment success state
  useCustomEventListener(CustomEvent.PaymentSaved, () => {
    queryClient.invalidateQueries([QueryKeys.Bills])
  })

  return (
    <Table
      columns={columns}
      currentPage={pagination?.page}
      data={data}
      id={TableIds.BillsList}
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
